import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Import components
import Navigation from './components/Navigation';
import ManagerNav from './components/ManagerNav';
import ManagerRouteGuard from './components/ManagerRouteGuard';
import OwnerRouteGuard from './components/OwnerRouteGuard';
import Home from './pages/Home';
import RestaurantLogin from './pages/RestaurantLogin';
import AdminDashboard from './pages/AdminDashboard';
import MenuManagement from './pages/MenuManagement';
import CustomerMenu from './pages/CustomerMenu';
import Checkout from './pages/Checkout';
import OwnerStats from './pages/OwnerStats';
import ManagerLogin from './pages/ManagerLogin';
import ManagerOrders from './pages/OrdersManagement';
import ManagerOrdersPage from './pages/ManagerOrdersPage';
import ManagerTables from './pages/ManagerTables';
import AdminManagers from './pages/AdminManagers';
import AdminWaiters from './pages/AdminWaiters';
import PromotionManagement from './pages/PromotionManagement';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import PWAInstallPrompt from './components/PWAInstallPrompt';

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const [subdomain, setSubdomain] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  // Функция для получения субдомена
  const getSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // Проверяем формат: subdomain.menu.qr-code.by
    if (parts.length >= 3 && parts[1] === 'menu') {
      return parts[0];
    }
    return null;
  };

  // Загрузка информации о ресторане по субдомену
  useEffect(() => {
    const currentSubdomain = getSubdomain();
    
    if (currentSubdomain) {
      setSubdomain(currentSubdomain);
      
      // Загрузить информацию о ресторане
      fetch(`/api/restaurant/subdomain/${currentSubdomain}`)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Restaurant not found');
        })
        .then(data => {
          setRestaurant(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading restaurant:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const hideNav = location.pathname === '/' || location.pathname.startsWith('/menu/') || location.pathname === '/checkout' || location.pathname === '/manager/orders-page';
  const isManagerRoute = location.pathname.startsWith('/manager');
  
  // Check if user is logged in as manager or owner
  const isManagerLoggedIn = localStorage.getItem('managerToken');
  const isOwnerLoggedIn = localStorage.getItem('restaurantToken');
  const isAdminMenuRoute = location.pathname === '/admin/menu';
  const isAdminPromotionsRoute = location.pathname === '/admin/promotions';

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="App">
      {!hideNav && !isManagerRoute && !(isAdminMenuRoute && isManagerLoggedIn) && !(isAdminPromotionsRoute && isManagerLoggedIn) && <Navigation />}
      {!hideNav && (isManagerRoute || (isAdminMenuRoute && isManagerLoggedIn) || (isAdminPromotionsRoute && isManagerLoggedIn)) && <ManagerNav />}
      <Routes>
        {subdomain ? (
          // Роуты для субдомена ресторана
          <>
            <Route 
              path="/" 
              element={
                <CustomerMenu 
                  subdomain={subdomain}
                  restaurant={restaurant}
                  isSubdomain={true}
                /> 
              } 
            />
            <Route 
              path="/delivery" 
              element={
                <CustomerMenu 
                  subdomain={subdomain}
                  restaurant={restaurant}
                  isSubdomain={true}
                  initialOrderType="delivery"
                /> 
              } 
            />
            <Route 
              path="/dine-in" 
              element={
                <CustomerMenu 
                  subdomain={subdomain}
                  restaurant={restaurant}
                  isSubdomain={true}
                  initialOrderType="dine_in"
                /> 
              } 
            />
            <Route 
              path="/checkout" 
              element={<Checkout />} 
            />
          </>
        ) : (
          // Обычные роуты для основного домена
          <>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<RestaurantLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/menu" element={
              <OwnerRouteGuard>
                <MenuManagement />
              </OwnerRouteGuard>
            } />
            <Route path="/admin/orders" element={<ManagerOrders />} />
            <Route path="/admin/managers" element={
              <OwnerRouteGuard>
                <AdminManagers />
              </OwnerRouteGuard>
            } />
            <Route path="/admin/promotions" element={
              <OwnerRouteGuard>
                <PromotionManagement />
              </OwnerRouteGuard>
            } />
            <Route path="/admin/stats" element={<OwnerStats />} />
            <Route path="/menu/:restaurantId" element={<CustomerMenu />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/manager/login" element={<ManagerLogin />} />
            <Route path="/manager/" element={
              <ManagerRouteGuard>
                <ManagerOrdersPage />
              </ManagerRouteGuard>
            } />
            <Route path="/manager/orders" element={
              <ManagerRouteGuard requireEditRights={true}>
                <ManagerOrders />
              </ManagerRouteGuard>
            } />
            <Route path="/manager/orders-page" element={
              <ManagerRouteGuard>
                <ManagerOrdersPage />
              </ManagerRouteGuard>
            } />
            <Route path="/manager/tables" element={
              <ManagerRouteGuard requireEditRights={true}>
                <ManagerTables />
              </ManagerRouteGuard>
            } />
            <Route path="/manager/waiters" element={
              <ManagerRouteGuard requireAdministrator={true}>
                <AdminWaiters />
              </ManagerRouteGuard>
            } />
            <Route path="/superadmin/login" element={<SuperAdminLogin />} />
            <Route path="/superadmin" element={<SuperAdminDashboard />} />
          </>
        )}
      </Routes>
      <PWAUpdatePrompt />
      <PWAInstallPrompt />
    </div>
  );
}

export default App;